<template>
  <section v-if="showSection" class="hero-text-section py-5">
    <div class="container-fluid">
      <div class="hero-text-wrapper">
        <!-- Background Elements -->
        <div class="floating-shapes">
          <div class="shape shape-1"></div>
          <div class="shape shape-2"></div>
          <div class="shape shape-3"></div>
          <div class="shape shape-4"></div>
        </div>

        <!-- Main Text Content -->
        <div class="row justify-content-center">
          <div class="col-12 col-md-10 col-lg-8 col-xl-7">
            <div class="hero-content text-center header-content">
              <!-- Company Name with Custom Styling -->
              <h1
                class="hero-company-name company-name"
                :data-aos="animationEffect"
                :data-aos-duration="animationDuration"
                :style="heroTitleStyle"
              >
                <span :class="{ 'text-gradient': titleGradientEnabled }">{{ companyName }}</span>
              </h1>

              <!-- Tagline with Typewriter Effect -->
              <h2
                class="hero-tagline tagline"
                data-aos="fade-up"
                data-aos-delay="300"
                data-aos-duration="1000"
                :style="heroSubtitleStyle"
              >
                <span class="typewriter" :class="{ 'text-gradient': subtitleGradientEnabled }">{{ tagline }}</span>
              </h2>

              <!-- Motto with Pulse Effect -->
              <p
                class="hero-motto motto"
                data-aos="fade-up"
                data-aos-delay="600"
                data-aos-duration="1000"
                :style="heroTextStyle"
              >
                <span class="pulse-text" :class="{ 'text-gradient': textGradientEnabled }">{{ motto }}</span>
              </p>

              <!-- Decorative Elements -->
              <div class="hero-decorations" data-aos="fade-up" data-aos-delay="900">
                <div class="decoration-line left"></div>
                <div class="decoration-star">✨</div>
                <div class="decoration-line right"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  showSection: boolean
  companyName: string
  tagline: string
  motto: string
  // Title styling props
  titleFontFamily?: string
  titleFontWeight?: string | number
  titleFontSize?: number
  titleLetterSpacing?: number
  titleTextTransform?: string
  titleTextShadow?: string
  titleGradientEnabled?: boolean
  titleGradientColor1?: string
  titleGradientColor2?: string
  titleGradientAngle?: number
  // Subtitle styling props
  subtitleFontFamily?: string
  subtitleFontWeight?: string | number
  subtitleFontSize?: number
  subtitleLetterSpacing?: number
  subtitleTextTransform?: string
  subtitleTextShadow?: string
  subtitleGradientEnabled?: boolean
  subtitleGradientColor1?: string
  subtitleGradientColor2?: string
  subtitleGradientAngle?: number
  // Text styling props
  textFontFamily?: string
  textFontWeight?: string | number
  textFontSize?: number
  textLetterSpacing?: number
  textTextTransform?: string
  textTextShadow?: string
  textGradientEnabled?: boolean
  textGradientColor1?: string
  textGradientColor2?: string
  textGradientAngle?: number
  // Animation props (common)
  animationEffect?: string
  animationDuration?: number
}

const props = withDefaults(defineProps<Props>(), {
  // Title defaults
  titleFontFamily: "'Inter Variable', 'Inter', sans-serif",
  titleFontWeight: '700',
  titleFontSize: 3.5,
  titleLetterSpacing: 0.05,
  titleTextTransform: 'none',
  titleTextShadow: 'soft',
  titleGradientEnabled: true,
  titleGradientColor1: '#3b82f6',
  titleGradientColor2: '#2563eb',
  titleGradientAngle: 135,
  // Subtitle defaults
  subtitleFontFamily: "'Inter Variable', 'Inter', sans-serif",
  subtitleFontWeight: '600',
  subtitleFontSize: 2,
  subtitleLetterSpacing: 0,
  subtitleTextTransform: 'none',
  subtitleTextShadow: 'none',
  subtitleGradientEnabled: false,
  subtitleGradientColor1: '#3b82f6',
  subtitleGradientColor2: '#2563eb',
  subtitleGradientAngle: 135,
  // Text defaults
  textFontFamily: "'Inter Variable', 'Inter', sans-serif",
  textFontWeight: '400',
  textFontSize: 1.25,
  textLetterSpacing: 0,
  textTextTransform: 'none',
  textTextShadow: 'none',
  textGradientEnabled: false,
  textGradientColor1: '#3b82f6',
  textGradientColor2: '#2563eb',
  textGradientAngle: 135,
  // Animation defaults
  animationEffect: 'fade-up',
  animationDuration: 1000,
})

// Compute text shadow style
const getTextShadowStyle = (shadowType: string) => {
  const shadows: { [key: string]: string } = {
    none: 'none',
    soft: '2px 2px 4px rgba(0, 0, 0, 0.2)',
    medium: '3px 3px 6px rgba(0, 0, 0, 0.3)',
    strong: '4px 4px 8px rgba(0, 0, 0, 0.5)',
    glow: '0 0 10px rgba(59, 130, 246, 0.8), 0 0 20px rgba(10, 69, 99, 0.6)',
    neon: '0 0 5px #fff, 0 0 10px #fff, 0 0 15px #3b82f6, 0 0 20px #3b82f6, 0 0 30px #2563eb, 0 0 40px #2563eb',
  }
  return shadows[shadowType] || shadows.soft
}

// Computed style for title
const heroTitleStyle = computed(() => {
  const style: any = {
    fontFamily: props.titleFontFamily,
    fontWeight: props.titleFontWeight,
    fontSize: `${props.titleFontSize}rem`,
    letterSpacing: `${props.titleLetterSpacing}rem`,
    textTransform: props.titleTextTransform,
    textShadow: getTextShadowStyle(props.titleTextShadow),
  }

  if (props.titleGradientEnabled) {
    style['--gradient-color1'] = props.titleGradientColor1
    style['--gradient-color2'] = props.titleGradientColor2
    style['--gradient-angle'] = `${props.titleGradientAngle}deg`
  }

  return style
})

// Computed style for subtitle
const heroSubtitleStyle = computed(() => {
  const style: any = {
    fontFamily: props.subtitleFontFamily,
    fontWeight: props.subtitleFontWeight,
    fontSize: `${props.subtitleFontSize}rem`,
    letterSpacing: `${props.subtitleLetterSpacing}rem`,
    textTransform: props.subtitleTextTransform,
    textShadow: getTextShadowStyle(props.subtitleTextShadow),
  }

  if (props.subtitleGradientEnabled) {
    style['--gradient-color1'] = props.subtitleGradientColor1
    style['--gradient-color2'] = props.subtitleGradientColor2
    style['--gradient-angle'] = `${props.subtitleGradientAngle}deg`
  }

  return style
})

// Computed style for text
const heroTextStyle = computed(() => {
  const style: any = {
    fontFamily: props.textFontFamily,
    fontWeight: props.textFontWeight,
    fontSize: `${props.textFontSize}rem`,
    letterSpacing: `${props.textLetterSpacing}rem`,
    textTransform: props.textTextTransform,
    textShadow: getTextShadowStyle(props.textTextShadow),
  }

  if (props.textGradientEnabled) {
    style['--gradient-color1'] = props.textGradientColor1
    style['--gradient-color2'] = props.textGradientColor2
    style['--gradient-angle'] = `${props.textGradientAngle}deg`
  }

  return style
})
</script>

<style scoped>
.hero-text-wrapper {
  position: relative;
  padding: 4rem 0;
}

/* Floating Shapes Background */
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
}

.shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
  animation: float 20s infinite ease-in-out;
}

.shape-1 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  top: -100px;
  left: -50px;
  animation-delay: 0s;
}

.shape-2 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  top: 50%;
  right: -30px;
  animation-delay: 5s;
}

.shape-3 {
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  bottom: -50px;
  left: 20%;
  animation-delay: 10s;
}

.shape-4 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  top: 20%;
  right: 15%;
  animation-delay: 15s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-30px) rotate(5deg);
  }
  50% {
    transform: translateY(0) rotate(0deg);
  }
  75% {
    transform: translateY(30px) rotate(-5deg);
  }
}

/* Hero Content */
.hero-content {
  position: relative;
  z-index: 1;
}

.hero-company-name {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  margin-bottom: 1.5rem;
  letter-spacing: -1px;
}

.text-gradient {
  background: linear-gradient(
    var(--gradient-angle, 135deg),
    var(--gradient-color1, #3b82f6) 0%,
    var(--gradient-color2, #2563eb) 50%,
    var(--gradient-color1, #f093fb) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 8s ease infinite;
  background-size: 200% auto;
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.hero-tagline {
  font-size: clamp(1.25rem, 3vw, 2rem);
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.typewriter {
  display: inline-block;
  border-right: 3px solid #3b82f6;
  animation: blink 0.7s step-end infinite;
}

@keyframes blink {
  from,
  to {
    border-color: transparent;
  }
  50% {
    border-color: #3b82f6;
  }
}

.hero-motto {
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: #6c757d;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.pulse-text {
  display: inline-block;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Decorative Elements */
.hero-decorations {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.decoration-line {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, #3b82f6, transparent);
}

.decoration-star {
  font-size: 1.5rem;
  animation: twinkle 2s ease-in-out infinite;
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .hero-text-wrapper {
    padding: 2rem 0;
  }

  .shape {
    opacity: 0.05;
  }

  .decoration-line {
    width: 40px;
  }
}
</style>
